const TURNSTILE_SCRIPT_SOURCE =
  'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit'

const TURNSTILE_SCRIPT_ID = 'cf-turnstile-script'

/**
 * Public Turnstile site key. Overridable via Vite env for staging widgets.
 * Site keys are not secrets — they are meant to ship in the browser.
 *
 * @returns {string} The site key to embed in the Turnstile widget.
 */
const getTurnstileSiteKey = (): string => {
  const fromEnvironment = import.meta.env.VITE_TURNSTILE_SITE_KEY
  if (typeof fromEnvironment === 'string' && fromEnvironment.length > 0) {
    return fromEnvironment
  }
  return '0x4AAAAAAECVoNctlIMosete'
}

/**
 * Ensures the Turnstile client script is present exactly once, then resolves
 * when `window.turnstile` is available for explicit widget renders.
 *
 * @returns {Promise<void>} Resolves when the Turnstile API is ready.
 */
const ensureTurnstileScript = (): Promise<void> => {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('Turnstile requires a browser environment'))
  }

  if (window.turnstile) {
    return Promise.resolve()
  }

  const existingScript = document.getElementById(TURNSTILE_SCRIPT_ID)
  if (existingScript) {
    return new Promise((resolve, reject) => {
      existingScript.addEventListener('load', () => resolve(), { once: true })
      existingScript.addEventListener(
        'error',
        () => reject(new Error('Failed to load Turnstile script')),
        { once: true },
      )
    })
  }

  return new Promise((resolve, reject) => {
    const script = document.createElement('script')
    script.id = TURNSTILE_SCRIPT_ID
    script.src = TURNSTILE_SCRIPT_SOURCE
    script.async = true
    script.defer = true
    script.onload = () => resolve()
    script.onerror = () => reject(new Error('Failed to load Turnstile script'))
    document.head.appendChild(script)
  })
}

export { ensureTurnstileScript, getTurnstileSiteKey }
